
// Components
import InfoTooltip from 'components/InfoTooltip';
import { Card } from 'components/ui/card';

// Utility
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';

export default function OrderReceipt(props) {
    const {
        tranId,
        event,
        discount,
        cart,
        ticketCount,
        subTotal,
        discountTotal,
        serviceFee,
        creditCardFee,
        tax,
        total,
        unifiedFee,
        taxName,
        discountsApplied,
    } = props;

    const { i18n } = useLingui();

    const formatCurrency = (value) =>
        i18n.number(value, { style: 'currency', currency: event.currency || 'CAD' });

    return (
        <Card className="my-6 w-full border-solid border-border bg-card text-card-foreground shadow-none">
            {tranId ? <p className='text-lg text-center mt-4'><Trans>Order Number: F{tranId}</Trans></p> : null}
            <div className="mx-4 my-8">
                <table className="w-full border-collapse">
                    <tbody>
                        {cart.map((product, index) => {
                            if (product.qty > 0) {
                                return (
                                    <tr key={index}>
                                        <td className="border-0 pb-4 pt-0 text-foreground">{`${product.name} x${product.qty}`}</td>
                                        <td className="border-0 pb-4 pt-0 text-right text-foreground">
                                            {formatCurrency(product.price * product.qty)}
                                        </td>
                                    </tr>
                                );
                            } else {
                                return null;
                            }
                        })}
                        {subTotal &&
                            ((event.taxable && !event.taxIncluded) ||
                                !event.hostAbsorbsB4tFees ||
                                !event.hostAbsorbsCcFees) ? (
                            <tr>
                                <td className="border-0 border-t border-solid border-border pb-8 pt-4 text-foreground">
                                    <b>
                                        <Trans>Sub Total</Trans>
                                    </b>
                                </td>
                                <td className="border-0 border-t border-solid border-border pb-8 pt-4 text-right text-foreground">
                                    <b>{formatCurrency(subTotal)}</b>
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>

                <table className="w-full border-collapse">
                    <tbody>
                        {discount ? (
                            <tr>
                                <td className="border-0 pb-4 pt-0 align-top text-primary">
                                    <p className="m-0">
                                        <Trans>Discount</Trans>
                                    </p>
                                    {typeof discountsApplied === 'number' &&
                                        Boolean(discount.perItemDiscount) === true && (
                                            <p className="m-0 text-xs">
                                                (
                                                {t`applied to ${discountsApplied} of ${ticketCount} items`}
                                                )
                                            </p>
                                        )}
                                </td>
                                <td className="border-0 pb-4 pt-0 text-right align-top text-primary">
                                    - {formatCurrency(discountTotal)}
                                </td>
                            </tr>
                        ) : null}
                        {subTotal && event.taxable && !event.taxIncluded ? (
                            <tr>
                                <td className="border-0 pb-4 pt-0 text-foreground">{taxName}</td>
                                <td className="border-0 pb-4 pt-0 text-right text-foreground">
                                    {formatCurrency(tax)}
                                </td>
                            </tr>
                        ) : null}

                        {event.province === 'QC' ? (
                            <>
                                {subTotal &&
                                    (!event.hostAbsorbsB4tFees || !event.hostAbsorbsCcFees) ? (
                                    <tr>
                                        <td className="border-0 pb-4 pt-0 text-foreground">
                                            <div className="flex items-center">
                                                <span className="mr-[0.35rem]">
                                                    <Trans>FD+ Fee</Trans>
                                                </span>
                                                <InfoTooltip
                                                    message={t`This fee helps support the technology and team behind your FrontDoor+ experience - from reliable ticketing to smooth check-ins at the event.`}
                                                />
                                            </div>
                                        </td>
                                        <td className="border-0 pb-4 pt-0 text-right text-foreground">
                                            {formatCurrency(unifiedFee)}
                                        </td>
                                    </tr>
                                ) : null}
                            </>
                        ) : (
                            <>
                                {subTotal && !event.hostAbsorbsB4tFees ? (
                                    <tr>
                                        <td className="border-0 pb-4 pt-0 text-foreground">
                                            <div className="flex items-center">
                                                <span className="mr-[0.35rem]">
                                                    <Trans>Convenience Fee</Trans>
                                                </span>
                                                <InfoTooltip
                                                    message={t`This fee helps support the technology and team behind your FrontDoor+ experience - from reliable ticketing to smooth check-ins at the event.`}
                                                />
                                            </div>
                                        </td>
                                        <td className="border-0 pb-4 pt-0 text-right text-foreground">
                                            {formatCurrency(serviceFee)}
                                        </td>
                                    </tr>
                                ) : null}
                                {subTotal && !event.hostAbsorbsCcFees ? (
                                    <tr>
                                        <td className="border-0 pb-4 pt-0 text-foreground">
                                            <div className="flex items-center">
                                                <span className="mr-[0.35rem]">
                                                    <Trans>Credit Card Fee</Trans>
                                                </span>
                                                <InfoTooltip
                                                    message={t`This fee goes directly to the payment processor, not FrontDoor+, to securely handle your credit card transaction.`}
                                                />
                                            </div>
                                        </td>
                                        <td className="border-0 pb-4 pt-0 text-right text-foreground">
                                            {formatCurrency(creditCardFee)}
                                        </td>
                                    </tr>
                                ) : null}
                            </>
                        )}

                        <tr>
                            <td className="border-0 border-t border-solid border-border pb-0 pt-4 text-foreground">
                                <b>
                                    <Trans>Total</Trans>
                                </b>
                            </td>
                            <td className="border-0 border-t border-solid border-border pb-0 pt-4 text-right text-foreground">
                                <b>{formatCurrency(total)}</b>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </Card>
    );
}
